'use client';

import React from 'react';
import { motion } from 'framer-motion';
import Image from 'next/image';

const clients = [
  { name: 'Activision', logo: '/images/clients/Activision.svg', delay: 0 },
  { name: 'EA', logo: '/images/clients/EA.svg', delay: 0.2 },
  { name: 'Valve', logo: '/images/clients/Valve.svg', delay: 0.4 },
  { name: 'Treyarch', logo: '/images/clients/Treyarch.svg', delay: 0.6 },
  { name: 'MTV', logo: '/images/clients/Mtv.svg', delay: 0.8 },
  { name: 'KW', logo: '/images/clients/KW.svg', delay: 1.0 },
];

const ClientOrbits = () => {
  return (
    <div className="pointer-events-none relative flex h-96 w-full items-center justify-center">
      
      <div className="pointer-events-none absolute z-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-center"
        >
        </motion.div>
      </div>

      {/* Orbiting client logos */}
      {clients.map((client, index) => {
        const angle = index * 60 * (Math.PI / 180); 
        const radius = 120; 
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;

        return (
          <motion.div
            key={client.name}
            className="absolute"
            initial={{ opacity: 0, scale: 0 }}
            whileInView={{
              opacity: 1,
              scale: 1,
              x: x,
              y: y,
            }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{
              duration: 0.8,
              delay: client.delay,
              type: 'spring',
              stiffness: 100,
            }}
            whileHover={{
              scale: 1.2,
              z: 50,
              transition: { duration: 0.2 },
            }}
            style={{
              transformOrigin: 'center',
            }}
          >
            <motion.div
              className="group relative"
              whileInView={{
                y: [0, -10, 0],
              }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{
                duration: 3,
                delay: client.delay,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            >
              {/* Logo container with glow effect */}
              <div className="relative">

                {/* Logo image */}
                <div className="relative z-30 flex h-16 w-16 items-center justify-center md:h-20 md:w-20">
                  <Image
                    src={client.logo}
                    alt={client.name}
                    width={80}
                    height={80}
                    className="relative z-30 h-full w-full object-contain"
                  />
                </div>
              </div>

              {/* Client name tooltip */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileHover={{ opacity: 1, y: 0 }}
                className="text-muted-foreground absolute -bottom-8 left-1/2 -translate-x-1/2 transform text-xs font-medium whitespace-nowrap opacity-0 transition-all duration-300 group-hover:opacity-100"
              >
                {client.name}
              </motion.div>
            </motion.div>
          </motion.div>
        );
      })}

      {/* Connecting lines between logos */}
      {clients.map((_, index) => {
        const angle1 = index * 60 * (Math.PI / 180);
        const angle2 = (index + 1) * 60 * (Math.PI / 180);
        const radius = 120;
        const x1 = Math.cos(angle1) * radius;
        const y1 = Math.sin(angle1) * radius;
        const x2 = Math.cos(angle2) * radius;
        const y2 = Math.sin(angle2) * radius;

        return (
          <svg
            key={`line-${index}`}
            className="pointer-events-none absolute"
            width="100%"
            height="100%"
            style={{
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            <motion.line
              x1={x1 + 50}
              y1={y1 + 50}
              x2={x2 + 50}
              y2={y2 + 50}
              stroke="url(#lineGradient)"
              strokeWidth="1"
              strokeDasharray="5,5"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1, delay: 1.5 + index * 0.1 }}
              className="opacity-30"
            />
          </svg>
        );
      })}

      {/* Gradient definitions for connecting lines */}
      <svg className="hidden">
        <defs>
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="hsl(var(--primary))" />
            <stop offset="50%" stopColor="hsl(var(--secondary))" />
            <stop offset="100%" stopColor="hsl(var(--accent))" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
};

export default ClientOrbits;
